import { LegalLayout, LegalSection } from "./LegalPage";

/**
 * Privacy Policy — standard SaaS template tailored to UAIP: OAuth-only sign-in
 * (no passwords stored), crawl/test data, Stripe billing, GCP hosting in
 * asia-southeast1, and user-configured Slack/Jira/Linear integrations.
 */
export function PrivacyPage(): JSX.Element {
  return (
    <LegalLayout title="Privacy Policy" updated="3 July 2026">
      <p>
        This Privacy Policy explains what information UAIP — operated by NNG IT
        Solutions ("we", "us", "our") — collects, how we use it, and the choices
        you have. It applies to the UAIP web app and API.
      </p>

      <LegalSection heading="1. Information we collect">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="text-slate-200">Account information.</strong> When
            you sign in with Google or GitHub, we receive your email address,
            name, and avatar from that provider. We do <em>not</em> receive or
            store your provider password — sign-in uses OAuth.
          </li>
          <li>
            <strong className="text-slate-200">Crawl &amp; test data.</strong>{" "}
            The URLs you crawl, the resulting site maps, generated tests, run
            results, and related metadata you create in the Service.
          </li>
          <li>
            <strong className="text-slate-200">Billing information.</strong> If
            you subscribe, our payment processor Stripe handles your card
            details. We store only a customer/subscription identifier and plan
            status — never full card numbers.
          </li>
          <li>
            <strong className="text-slate-200">Integration credentials.</strong>{" "}
            If you connect Slack, Jira, Linear, or GitHub, we store the
            webhook URL, API token, or installation token you provide, so we can
            act on your behalf.
          </li>
          <li>
            <strong className="text-slate-200">Usage &amp; technical data.</strong>{" "}
            Basic logs such as IP address, request times, and error diagnostics,
            used to operate and secure the Service.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. How we use information">
        <ul className="list-disc space-y-1 pl-5">
          <li>To provide, maintain, and improve the Service;</li>
          <li>To authenticate you and keep your account secure;</li>
          <li>To process subscriptions and enforce plan limits;</li>
          <li>To deliver the integrations you configure;</li>
          <li>To detect, prevent, and respond to abuse or technical issues;</li>
          <li>To comply with legal obligations.</li>
        </ul>
        <p>We do not sell your personal information.</p>
      </LegalSection>

      <LegalSection heading="3. Cookies">
        <p>
          We use a single, essential session cookie to keep you signed in after
          OAuth. It is HTTP-only and does not track you across other sites. We do
          not use third-party advertising cookies.
        </p>
      </LegalSection>

      <LegalSection heading="4. Service providers we share with">
        <p>
          We share data only with providers that help us run the Service, under
          agreements that protect your information:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="text-slate-200">Google Cloud Platform</strong> —
            hosting, database, and storage (region: asia-southeast1, Singapore);
          </li>
          <li>
            <strong className="text-slate-200">Google / GitHub</strong> — OAuth
            sign-in;
          </li>
          <li>
            <strong className="text-slate-200">Stripe</strong> — payment
            processing;
          </li>
          <li>
            <strong className="text-slate-200">Integrations you enable</strong>{" "}
            (Slack, Jira, Linear, GitHub) — only the data needed to fulfil the
            action you request.
          </li>
        </ul>
        <p>
          We may also disclose information if required by law or to protect the
          rights and safety of our users and the Service.
        </p>
      </LegalSection>

      <LegalSection heading="5. Data retention">
        <p>
          We keep your data for as long as your account is active. When you
          delete your account, we delete your associated data — including
          projects, crawls, runs, and connected integrations — except where we
          must retain limited records to meet legal or accounting obligations.
        </p>
      </LegalSection>

      <LegalSection heading="6. Security">
        <p>
          Data is encrypted in transit (HTTPS) and at rest on our cloud
          provider, and the database is reachable only over a private connection.
          No system is perfectly secure, but we take reasonable measures to
          protect your information.
        </p>
      </LegalSection>

      <LegalSection heading="7. Your rights">
        <p>
          You can access and update your account information, export or delete
          your data, and disconnect integrations at any time from within the
          app. To request full account deletion or ask a privacy question, email{" "}
          <a className="text-slate-200 hover:text-white" href="mailto:info@uaip.dev">
            info@uaip.dev
          </a>
          . Depending on where you live, you may have additional rights
          (e.g. access, correction, erasure, or objection) under applicable data
          protection law.
        </p>
      </LegalSection>

      <LegalSection heading="8. International users">
        <p>
          Our infrastructure is hosted in Singapore (GCP asia-southeast1). If you
          access UAIP from elsewhere, you understand your data will be processed
          there.
        </p>
      </LegalSection>

      <LegalSection heading="9. Children">
        <p>
          UAIP is not directed to children under 16, and we do not knowingly
          collect their personal information.
        </p>
      </LegalSection>

      <LegalSection heading="10. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. If we make
          material changes, we will take reasonable steps to notify you and
          update the "Last updated" date above.
        </p>
      </LegalSection>

      <LegalSection heading="11. Contact">
        <p>
          NNG IT Solutions · UAIP —{" "}
          <a className="text-slate-200 hover:text-white" href="mailto:info@uaip.dev">
            info@uaip.dev
          </a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
